import { zodResolver } from "@hookform/resolvers/zod";
import { Plus, Save, Trash2, Undo, X } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { useFieldArray, useForm, useWatch } from "react-hook-form";
import { toast } from "sonner";
import { validateExpression } from "@globals/rules";
import { getMqttFirstValues, getNotificationRules, postNotificationRules } from "@client/api/endpoints";
import { useAsync } from "@client/hooks/useAsync";
import { useMutation } from "@client/hooks/useMutation";
import { t } from "@client/lib/dictionary";
import { ruleSchema, type RuleFormValues } from "@client/lib/schemas/rule";
import { Badge } from "@client/components/ui/badge";
import { Button } from "@client/components/ui/button";
import { Card, CardContent } from "@client/components/ui/card";
import { Input } from "@client/components/ui/input";
import { Label } from "@client/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@client/components/ui/select";
import { Spinner } from "@client/components/ui/spinner";

interface RulesTabPanelProps {
  onClose?: () => void;
}

const blankRule: RuleFormValues = {
  id: 0,
  name: "Nové pravidlo",
  notificationTitle: "Stalo se něco",
  notificationBody: "Hodnota: {zige/pozar0/temp/val}°C",
  severity: "INFO",
  conditions: [{ condition: "{zige/pozar0/temp/val} > 30" }],
};

interface RuleEditableLocal extends RuleFormValues {
  status: "saved" | "new" | "edited" | "deleted";
}

const RulesTabPanel: React.FC<RulesTabPanelProps> = () => {
  const { data: topicsResp, loading: topicsLoading } = useAsync(getMqttFirstValues, []);
  const { data: rulesResp, loading: rulesLoading, refetch: refetchRules } = useAsync(getNotificationRules, []);

  const topics: RuleTopics = useMemo(() => {
    const t: RuleTopics = {};
    if (topicsResp?.responseObject) {
      for (const v of topicsResp.responseObject) {
        t[v.topicId] = v.topic.valueType === "FLOAT" ? "number" : "boolean";
      }
    }
    return t;
  }, [topicsResp]);

  const [rules, setRules] = useState<RuleEditableLocal[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  // Inicializace rules po fetchi
  useEffect(() => {
    if (rulesResp?.responseObject) {
      const init: RuleEditableLocal[] = rulesResp.responseObject.map((r) => ({
        id: r.id,
        name: r.name,
        notificationTitle: r.notificationTitle,
        notificationBody: r.notificationBody,
        severity: r.severity,
        conditions: r.conditions.map((c) => ({ condition: c })),
        status: "saved",
      }));
      // Synchronizace fetched server dat do lokálního editable state - server data jsou
      // external system, local state má dodatečnou strukturu (`status` per pravidlo).
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setRules(init);
      setSelectedId(init[0]?.id ?? null);
    }
  }, [rulesResp]);

  const selectedRule = rules.find((r) => r.id === selectedId);

  const form = useForm<RuleFormValues>({
    resolver: zodResolver(ruleSchema),
    values: selectedRule
      ? {
          id: selectedRule.id,
          name: selectedRule.name,
          notificationTitle: selectedRule.notificationTitle,
          notificationBody: selectedRule.notificationBody,
          severity: selectedRule.severity,
          conditions: selectedRule.conditions,
        }
      : undefined,
  });

  const { fields, append, remove } = useFieldArray({ control: form.control, name: "conditions" });

  const { mutate: saveRules, loading: saving } = useMutation(postNotificationRules);

  // Synchronizace formulářových změn zpět do `rules` (live diff).
  // useWatch (vs form.watch()) je memoizable a kompatibilní s React Compiler.
  const watchedRaw = useWatch({ control: form.control });
  // Re-asserce typu - `useWatch` deklaruje vše Partial; my víme, že po `values=...` zod má všechno.
  const watchedValues = watchedRaw as RuleFormValues | undefined;
  useEffect(() => {
    if (!selectedId || !watchedValues) return;
    const original = rulesResp?.responseObject?.find((r) => r.id === selectedId);
    // Sanitizujeme conditions na non-optional condition (default ""), zod by stejně zachytil empty.
    const safeConditions: { condition: string }[] = (watchedValues.conditions ?? []).map((c) => ({
      condition: c.condition ?? "",
    }));
    // Form watch je external system (RHF) - synchronizace do lokálního editable state
    // s diff statusem vůči serveru. setState v effect je tu záměrně.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setRules((prev) =>
      prev.map((r) => {
        if (r.id !== selectedId) return r;
        const merged: RuleEditableLocal = { ...r, ...watchedValues, conditions: safeConditions };
        if (r.status === "deleted") return r;
        if (r.status === "new") return merged;
        // "saved" → check diff
        const isSame =
          original &&
          original.name === merged.name &&
          original.notificationTitle === merged.notificationTitle &&
          original.notificationBody === merged.notificationBody &&
          original.severity === merged.severity &&
          original.conditions.length === merged.conditions.length &&
          original.conditions.every((c, i) => c === merged.conditions[i]?.condition);
        return { ...merged, status: isSame ? "saved" : "edited" };
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(watchedValues), selectedId]);

  const addRule = () => {
    const newId = Date.now();
    const newRule: RuleEditableLocal = { ...blankRule, id: newId, status: "new" };
    setRules((prev) => [...prev, newRule]);
    setSelectedId(newId);
  };

  const toggleDelete = (id: number) => {
    setRules((prev) =>
      prev
        .map((r) => {
          if (r.id !== id) return r;
          if (r.status === "new") return null; // úplně odstranit
          if (r.status === "deleted") return { ...r, status: "saved" as const };
          return { ...r, status: "deleted" as const };
        })
        .filter((r): r is RuleEditableLocal => r !== null)
    );
  };

  const hasChanges = rules.some((r) => r.status !== "saved");

  const onSave = async () => {
    // Validuj všechny podmínky
    for (const r of rules) {
      if (r.status === "deleted") continue;
      for (const c of r.conditions) {
        if (!validateExpression(c.condition, topics)) {
          toast.error(`${t.rules.syntaxError} (${r.name}: "${c.condition}")`);
          return;
        }
      }
    }

    const setRulesPayload: SetRules = {
      added: rules
        .filter((r) => r.status === "new")
        .map((r) => ({
          id: r.id,
          name: r.name,
          notificationTitle: r.notificationTitle,
          notificationBody: r.notificationBody,
          severity: r.severity,
          conditions: r.conditions.map((c) => c.condition),
          topicIds: [],
        })),
      edited: rules
        .filter((r) => r.status === "edited")
        .map((r) => ({
          id: r.id,
          name: r.name,
          notificationTitle: r.notificationTitle,
          notificationBody: r.notificationBody,
          severity: r.severity,
          conditions: r.conditions.map((c) => c.condition),
          topicIds: [],
        })),
      deleted: rules
        .filter((r) => r.status === "deleted")
        .map((r) => ({
          id: r.id,
          name: r.name,
          notificationTitle: r.notificationTitle,
          notificationBody: r.notificationBody,
          severity: r.severity,
          conditions: r.conditions.map((c) => c.condition),
          topicIds: [],
        })),
    };

    try {
      const res = await saveRules(setRulesPayload);
      if (res.success) {
        toast.success(t.rules.saved);
        await refetchRules();
      } else {
        toast.error(res.message || t.rules.saveError);
      }
    } catch {
      toast.error(t.rules.saveError);
    }
  };

  const loading = topicsLoading || rulesLoading;

  return (
    <div className="flex flex-col gap-3 max-h-[70vh] overflow-y-auto">
      <div className="flex flex-col md:flex-row gap-3">
        {/* Left: rule list */}
        <div className="md:w-1/3 flex flex-col gap-2">
          <h3 className="text-lg font-semibold">{t.rules.title}</h3>
          {loading && (
            <div className="flex justify-center py-4">
              <Spinner />
            </div>
          )}
          <ul className="flex flex-col gap-1.5">
            {rules.map((rule) => (
              <li key={rule.id}>
                <Card
                  className={`cursor-pointer transition-colors ${
                    selectedId === rule.id ? "ring-2 ring-ring" : ""
                  } ${rule.status === "deleted" ? "opacity-50" : ""}`}
                  onClick={() => setSelectedId(rule.id)}
                >
                  <CardContent className="flex items-center justify-between p-2 gap-2">
                    <div className="flex flex-col gap-0.5 flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`truncate ${rule.status === "deleted" ? "line-through" : ""}`}>
                          {rule.name}
                        </span>
                        {rule.status === "new" && <Badge variant="ok">nové</Badge>}
                        {rule.status === "edited" && <Badge variant="warning">upraveno</Badge>}
                        {rule.status === "deleted" && <Badge variant="serious">smazáno</Badge>}
                      </div>
                    </div>
                    <Button
                      variant={rule.status === "deleted" ? "ghost" : "destructive"}
                      size="icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleDelete(rule.id);
                      }}
                    >
                      {rule.status === "deleted" ? <Undo className="h-4 w-4" /> : <Trash2 className="h-4 w-4" />}
                    </Button>
                  </CardContent>
                </Card>
              </li>
            ))}
          </ul>
          <Button variant="outline" onClick={addRule} className="w-full">
            <Plus className="h-4 w-4" /> {t.rules.addNew}
          </Button>
        </div>

        {/* Right: editor */}
        <div className="md:w-2/3">
          {selectedRule && selectedRule.status !== "deleted" ? (
            <form className="flex flex-col gap-3">
              <div>
                <Label htmlFor="rule-name">{t.rules.name}</Label>
                <Input id="rule-name" {...form.register("name")} invalid={!!form.formState.errors.name} />
                {form.formState.errors.name && (
                  <p className="text-xs text-destructive mt-1">{form.formState.errors.name.message}</p>
                )}
              </div>
              <div>
                <Label htmlFor="rule-title">{t.rules.notificationTitle}</Label>
                <Input
                  id="rule-title"
                  {...form.register("notificationTitle")}
                  invalid={!!form.formState.errors.notificationTitle}
                />
              </div>
              <div>
                <Label htmlFor="rule-body">{t.rules.notificationBody}</Label>
                <Input
                  id="rule-body"
                  {...form.register("notificationBody")}
                  invalid={!!form.formState.errors.notificationBody}
                />
              </div>
              <div>
                <Label>{t.rules.severity}</Label>
                <Select
                  value={watchedValues?.severity ?? "INFO"}
                  onValueChange={(v) =>
                    form.setValue("severity", v as RuleFormValues["severity"], { shouldDirty: true })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="INFO">INFO</SelectItem>
                    <SelectItem value="WARNING">WARNING</SelectItem>
                    <SelectItem value="SERIOUS">SERIOUS</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t.rules.conditions}</Label>
                <ul className="flex flex-col gap-2">
                  {fields.map((field, idx) => (
                    <li key={field.id} className="flex gap-2">
                      <Input
                        {...form.register(`conditions.${idx}.condition`)}
                        invalid={!!form.formState.errors.conditions?.[idx]}
                      />
                      <Button type="button" variant="destructive" size="icon" onClick={() => remove(idx)}>
                        <X className="h-4 w-4" />
                      </Button>
                    </li>
                  ))}
                </ul>
                <Button type="button" variant="outline" className="mt-2" onClick={() => append({ condition: "" })}>
                  <Plus className="h-4 w-4" /> {t.rules.addCondition}
                </Button>
              </div>
            </form>
          ) : (
            <Card>
              <CardContent className="p-6 text-center text-muted-foreground">
                {selectedRule?.status === "deleted"
                  ? "Pravidlo je označeno ke smazání. Klikni na undo v levém panelu."
                  : "Vyber pravidlo nebo přidej nové."}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      <div className="flex justify-end gap-2 sticky bottom-0 bg-card pt-3">
        <Button variant="outline" onClick={() => refetchRules()}>
          {t.app.cancel}
        </Button>
        <Button onClick={onSave} disabled={!hasChanges || saving}>
          <Save className="h-4 w-4" /> {saving ? t.app.loading : t.app.save}
        </Button>
      </div>
    </div>
  );
};

export default React.memo(RulesTabPanel);
